/**
 * Browser Fingerprint Utility
 * Collects browser and device information for fraud prevention
 * This data helps Opayo assess transaction risk
 */

/**
 * Get browser fingerprint data
 * @returns {BrowserFingerprint} Browser and device information
 */
export const getBrowserFingerprint = () => {
  const fingerprint = {
    userAgent: navigator.userAgent || '',
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth ? `${window.screen.colorDepth}` : '24',
    timezone: new Date().getTimezoneOffset().toString(),
    language: navigator.language || navigator.userLanguage || 'en-US',
    javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : false
  };

  return fingerprint;
};

/**
 * Get additional device information for enhanced fraud detection
 * @returns {Object} Extended device information
 */
export const getExtendedFingerprint = () => {
  const basic = getBrowserFingerprint();
  
  return {
    ...basic,
    platform: navigator.platform || '',
    cookiesEnabled: navigator.cookieEnabled || false,
    doNotTrack: navigator.doNotTrack || 'unspecified',
    screenAvailableResolution: `${window.screen.availWidth}x${window.screen.availHeight}`,
    pixelRatio: window.devicePixelRatio || 1,
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
    deviceMemory: navigator.deviceMemory || 'unknown',
    connection: navigator.connection ? {
      effectiveType: navigator.connection.effectiveType || 'unknown',
      downlink: navigator.connection.downlink || 'unknown',
      rtt: navigator.connection.rtt || 'unknown'
    } : null
  };
};

/**
 * Generate a simple hash from fingerprint data (for logging/debugging)
 * @param {BrowserFingerprint} fingerprint - Fingerprint data
 * @returns {string} Simple hash string
 */
export const hashFingerprint = (fingerprint) => {
  const data = JSON.stringify(fingerprint);
  let hash = 0;
  
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

/**
 * Check if the browser environment is suitable for payment processing
 * @returns {Object} Environment check result
 */
export const checkBrowserCompatibility = () => {
  const issues = [];

  // Check for HTTPS (required for payment processing)
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    issues.push('HTTPS is required for secure payment processing');
  }

  // Check for JavaScript enabled (obviously true if this runs, but for completeness)
  if (typeof window === 'undefined') {
    issues.push('JavaScript must be enabled');
  }

  // Check for cookies enabled
  if (!navigator.cookieEnabled) {
    issues.push('Cookies must be enabled for payment processing');
  }

  // Check for local storage
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
  } catch (e) {
    issues.push('Local storage must be enabled');
  }

  // Check for modern browser features
  if (!window.Promise) {
    issues.push('Browser is too old - please update your browser');
  }

  return {
    isCompatible: issues.length === 0,
    issues
  };
};

/**
 * Get IP address information (client-side approximation)
 * Note: Actual IP should be captured server-side for security
 * @returns {Promise<Object>} IP information
 */
export const getIPInfo = async () => {
  try {
    // This is a placeholder - in production, IP should be captured server-side
    // You could use a service like ipapi.co or ipify
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    if (response.ok) {
      const data = await response.json();
      return { ip: data.ip };
    }
  } catch (error) {
    console.warn('Could not fetch IP information:', error);
  }
  
  return { ip: 'unknown' };
};

/**
 * Log fingerprint data (for debugging - remove in production)
 * @param {BrowserFingerprint} fingerprint - Fingerprint data
 */
export const logFingerprint = (fingerprint) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Browser Fingerprint:', fingerprint);
    console.log('Fingerprint Hash:', hashFingerprint(fingerprint));
  }
};

export default {
  getBrowserFingerprint,
  getExtendedFingerprint,
  hashFingerprint,
  checkBrowserCompatibility,
  getIPInfo,
  logFingerprint
};
